//generate react component for ContentCard with TypeScript and scss
import './CoralMasterCard.scss'

import { Asset } from '@websrc/App/Components/Asset/Asset'
import { CTA } from '@websrc/App/Components/CTA'
import { Markdown } from '@websrc/App/Components/Markdown/Markdown'
import { CoralMasterCardVisuals, mapCoralMasterCard } from '@websrc/DataMappers/mapCoralMasterCard'
import React, { useEffect, useRef, useState } from 'react'

import { CoralAttribute } from '../../../Attribute/CoralAttribute'
import { CoralBadge } from '../../../Badge/CoralBadge'

type CoralMasterCardProps = ReturnType<typeof mapCoralMasterCard> & {
  visuals?: CoralMasterCardVisuals
}

export const CoralMasterCard: React.FC<CoralMasterCardProps> = (props) => {
  const singleCtaRef = useRef<any>(null)
  const [isHovered, setIsHovered] = useState(false)
  const [isPressed, setIsPressed] = useState(false)
  const { visuals = {} } = props

  const hasContentAboveButtons =
    !!props.badge || !!props.subtitle || !!props.headline || !!props.body

  const triggerSingleCta = () => singleCtaRef.current?.click()

  //Set default visuals values here
  visuals.buttonTheme = 'white'

  useEffect(() => {
    const handleMouseUp = () => setIsPressed(false)
    window.addEventListener('mouseup', handleMouseUp)
    return () => {
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [])

  return (
    <div
      className={`master-card m--radius-${visuals?.radius} m--color-${
        visuals?.contentContainerColor
      } m--orientation-${visuals?.imageOrientation} m--clickable-${
        props.buttons?.length === 1
      } m--animation-${visuals?.hoverAnimation}`}
      onClick={() => triggerSingleCta()}
      onMouseDown={(e) => {
        if (singleCtaRef.current) {
          if (singleCtaRef.current.contains(e.target as Node)) return
          setIsPressed(true)
        }
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {props.asset && (
        <div
          className={`master-card__asset-container m--color-${visuals?.imageContainerBackground} m--equal-size-${visuals?.equalSize} m--border-around-image-${props.visuals?.imageBorder}`}
        >
          {props.assetLabelText && <span>{props.assetLabelText}</span>}
          <Asset
            altText={props.asset.altText}
            className={`master-card__asset m--ratio-${visuals?.imageRatio} m--border-around-image-${props.visuals?.imageBorder}`}
            objectFit={'cover'}
            thumbnail={props.asset.thumbnail}
          />
        </div>
      )}

      <div
        className={`master-card__content-container m--position-${visuals?.positionContent} m--equal-size-${props.visuals?.equalSize}`}
      >
        <div
          className={`master-card__content-inner-container m--padding-${visuals?.contentPadding}`}
        >
          {props.badge && <CoralBadge backgroundColor={props.badgeColor} text={props.badge} />}
          {props.subtitle && (
            <div
              className={`master-card__subhead m--size-${visuals?.subheadSize} master-card__-text-padding-${visuals?.paddingSubhead}`}
            >
              {props.subtitle}
            </div>
          )}
          {props.headline && (
            <div
              className={`master-card__headline m--size-${visuals?.headlineSize} master-card__text-padding-${visuals?.paddingHeadline}`}
              style={{
                WebkitLineClamp: visuals?.maxHeadlineLines,
                lineClamp: visuals?.maxHeadlineLines,
              }}
            >
              {props.headline}
            </div>
          )}
          {props.body && (
            <Markdown
              className={`master-card__body m--size-${visuals?.bodySize} master-card__text-padding-${visuals?.paddingBody}`}
              markdown={props.body}
              style={{
                WebkitLineClamp: visuals?.maxBodyLines,
                lineClamp: visuals?.maxBodyLines,
              }}
            />
          )}
          {props.buttons?.length > 0 && (
            <div
              className={`master-card__buttons ${
                visuals?.buttonHide ? 'm--is-visually-hidden' : ''
              } ${!hasContentAboveButtons ? 'm--no-padding-top' : ''}`}
            >
              {props.buttons.length === 1 ? (
                <div
                  className={`master-card__button m--width-auto m--position-${visuals.positionButton}`}
                >
                  <CTA
                    className={`coral-global-button m--type-${
                      visuals?.buttonType?.[0] || 'fill'
                    } m--width-${visuals?.buttonWidth} ${
                      isPressed ? 'm--simulate-pressed' : isHovered ? 'm--simulate-hover' : ''
                    }  m--theme-${visuals?.buttonTheme} ${
                      visuals.buttonIconDistance ? 'm--icon-distance' : ''
                    } m--size-${visuals?.buttonSize}`}
                    cta={props.buttons[0]}
                    iconDirection={visuals.buttonIconDirection}
                    iconName={visuals.buttonIcon}
                    ref={singleCtaRef}
                    type={'clean'}
                  />
                </div>
              ) : (
                <>
                  {props.buttons.map((button, index) => (
                    <div
                      className={`master-card__button m--position-${visuals.positionButton}`}
                      key={index}
                    >
                      <CTA
                        className={`coral-global-button m--type-${
                          visuals?.buttonType?.[index] || 'fill'
                        } m--theme-${visuals?.buttonTheme} ${
                          visuals.buttonIconDistance ? 'm--icon-distance' : ''
                        } m--size-${visuals?.buttonSize}`}
                        cta={button}
                        iconDirection={visuals.buttonIconDirection}
                        iconName={visuals.buttonIcon}
                        type={'clean'}
                      />
                    </div>
                  ))}
                </>
              )}
            </div>
          )}

          {props.attributes?.length > 0 && (
            <div className={`master-card__attributes`}>
              {props.attributes.map((attribute, id) => (
                <CoralAttribute
                  icon={attribute.attributeIcon}
                  key={id}
                  label={attribute.attributeLabel}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
